import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Building, Phone, Globe, Clock, MapPin, Save, ChevronDown, ChevronRight,
  Plus, Trash2, Star, ShoppingBag, MessageSquare, Users, Accessibility,
  Truck, UtensilsCrossed, Target, FileText, Loader2
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const DAYS = ['monday','tuesday','wednesday','thursday','friday','saturday','sunday'];

function Section({ title, icon: Icon, children, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <Card className="bg-white border-gray-200">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center gap-3 px-5 py-4 text-left hover:bg-white/[0.02] transition-colors" data-testid={`section-${title.toLowerCase().replace(/\s+/g, '-')}`}>
        <Icon className="h-4 w-4 text-blue-400 flex-shrink-0" />
        <span className="text-sm font-semibold text-gray-900 flex-1">{title}</span>
        {open ? <ChevronDown className="h-4 w-4 text-gray-500" /> : <ChevronRight className="h-4 w-4 text-gray-500" />}
      </button>
      {open && <CardContent className="pt-0 pb-5 px-5 border-t border-gray-200/50">{children}</CardContent>}
    </Card>
  );
}

function formatPhoneDisplay(val) {
  const digits = (val || '').replace(/\D/g, '');
  const d = digits.startsWith('1') && digits.length > 10 ? digits.slice(1) : digits;
  if (d.length <= 3) return d;
  if (d.length <= 6) return `(${d.slice(0,3)}) ${d.slice(3)}`;
  return `(${d.slice(0,3)}) ${d.slice(3,6)}-${d.slice(6,10)}`;
}

function Field({ label, value, onChange, type = 'text', placeholder = '', className = '' }) {
  const isTel = type === 'tel';
  const handleChange = (e) => {
    if (isTel) {
      const raw = e.target.value.replace(/\D/g, '');
      const trimmed = raw.startsWith('1') && raw.length > 10 ? raw.slice(1) : raw;
      onChange(formatPhoneDisplay(trimmed.slice(0, 10)));
    } else {
      onChange(e.target.value);
    }
  };
  return (
    <div className={className}>
      <Label className="text-xs text-gray-400 mb-1 block">{label}</Label>
      <Input value={isTel ? formatPhoneDisplay(value) : (value || '')} onChange={handleChange} type={isTel ? 'tel' : type} placeholder={placeholder || (isTel ? '(817) 555-1234' : '')}
        className="bg-gray-50 border-gray-200 text-gray-900 text-sm h-9 placeholder:text-gray-600" />
    </div>
  );
}

function TextArea({ label, value, onChange, rows = 3, placeholder = '' }) {
  return (
    <div>
      <Label className="text-xs text-gray-400 mb-1 block">{label}</Label>
      <textarea value={value || ''} onChange={e => onChange(e.target.value)} rows={rows} placeholder={placeholder}
        className="w-full bg-gray-50 border border-gray-200 text-gray-900 text-sm rounded-md px-3 py-2 resize-none focus:border-blue-500 focus:outline-none placeholder:text-gray-600" />
    </div>
  );
}

function Toggle({ label, checked, onChange }) {
  return (
    <label className="flex items-center gap-2 cursor-pointer">
      <div className={`w-8 h-4.5 rounded-full relative transition-colors ${checked ? 'bg-blue-600' : 'bg-gray-700'}`} onClick={() => onChange(!checked)}>
        <div className={`absolute top-0.5 w-3.5 h-3.5 rounded-full bg-white transition-transform ${checked ? 'translate-x-4' : 'translate-x-0.5'}`} />
      </div>
      <span className="text-sm text-gray-300">{label}</span>
    </label>
  );
}

function ListEditor({ label, items = [], onChange, placeholder = 'Add item...' }) {
  const [val, setVal] = useState('');
  const add = () => { if (val.trim()) { onChange([...items, val.trim()]); setVal(''); } };
  return (
    <div>
      <Label className="text-xs text-gray-400 mb-1 block">{label}</Label>
      <div className="flex gap-2 mb-2">
        <Input value={val} onChange={e => setVal(e.target.value)} placeholder={placeholder}
          className="bg-gray-50 border-gray-200 text-gray-900 text-sm h-8 flex-1 placeholder:text-gray-600"
          onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), add())} />
        <Button size="sm" onClick={add} className="bg-blue-600 hover:bg-blue-700 h-8 px-2"><Plus className="h-3 w-3" /></Button>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {items.map((item, i) => (
          <Badge key={i} className="bg-gray-800 text-gray-300 text-xs gap-1 pr-1">
            {item}
            <button onClick={() => onChange(items.filter((_, j) => j !== i))} className="text-gray-500 hover:text-red-400 ml-1"><Trash2 className="h-2.5 w-2.5" /></button>
          </Badge>
        ))}
      </div>
    </div>
  );
}

export default function ClientBusinessProfile({ client, getHeaders }) {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    fetch(`${API}/api/client/dashboard/business-profile`, { headers: getHeaders() })
      .then(r => r.json())
      .then(d => setProfile(d.profile))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [getHeaders]);

  const update = useCallback((key, val) => {
    setProfile(p => ({ ...p, [key]: val }));
    setSaved(false);
  }, []);

  const updateHours = useCallback((day, field, val) => {
    setProfile(p => ({
      ...p, hours: { ...p.hours, [day]: { ...p.hours?.[day], [field]: val } }
    }));
    setSaved(false);
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await fetch(`${API}/api/client/dashboard/business-profile`, {
        method: 'PUT',
        headers: { ...getHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(profile),
      });
      setSaved(true);
    } catch (e) { console.error(e); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="text-gray-400 animate-pulse py-12 text-center">Loading business profile...</div>;
  if (!profile) return <div className="text-red-400 text-center py-12">Failed to load profile</div>;

  return (
    <div className="space-y-3" data-testid="business-profile-tab">
      {/* Save bar */}
      <div className="flex items-center justify-between bg-white border border-gray-200 rounded-lg px-4 py-3">
        <p className="text-sm text-gray-400">Edit your business profile to improve directory accuracy and search visibility.</p>
        <Button onClick={save} disabled={saving || saved} className="bg-blue-600 hover:bg-blue-700 text-white text-xs h-8 px-4" data-testid="save-profile-btn">
          {saving ? <><Loader2 className="h-3 w-3 mr-1 animate-spin" /> Saving...</> : saved ? <><Save className="h-3 w-3 mr-1" /> Saved</> : <><Save className="h-3 w-3 mr-1" /> Save Changes</>}
        </Button>
      </div>

      {/* Core Information */}
      <Section title="Core Information" icon={Building} defaultOpen={true}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
          <Field label="Business Name" value={profile.business_name} onChange={v => update('business_name', v)} />
          <Field label="Business Type" value={profile.business_type} onChange={v => update('business_type', v)} placeholder="e.g., Plumbing, Restaurant" />
          <Field label="Entity ID" value={profile.entity_id} onChange={v => update('entity_id', v)} placeholder="Internal reference" />
          <Field label="Year Established" value={profile.year_established} onChange={v => update('year_established', v)} type="number" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
          <Field label="Address" value={profile.address} onChange={v => update('address', v)} className="md:col-span-2" />
          <Field label="City" value={profile.city} onChange={v => update('city', v)} />
          <div className="grid grid-cols-2 gap-3">
            <Field label="State" value={profile.state} onChange={v => update('state', v)} />
            <Field label="ZIP Code" value={profile.zip_code} onChange={v => update('zip_code', v)} />
          </div>
        </div>
      </Section>

      {/* Contact, Websites & Apps */}
      <Section title="Contact, Websites & Apps" icon={Globe}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
          <Field label="Primary Phone" value={profile.phone} onChange={v => update('phone', v)} type="tel" />
          <Field label="Alt Phone" value={profile.alt_phone} onChange={v => update('alt_phone', v)} type="tel" />
          <Field label="Email" value={profile.email} onChange={v => update('email', v)} type="email" />
          <Field label="Fax" value={profile.fax} onChange={v => update('fax', v)} type="tel" />
          <Field label="Primary Website" value={profile.website} onChange={v => update('website', v)} />
          <Field label="Appointment URL" value={profile.appointment_url} onChange={v => update('appointment_url', v)} />
          <Field label="iOS App URL" value={profile.mobile_app_ios} onChange={v => update('mobile_app_ios', v)} />
          <Field label="Android App URL" value={profile.mobile_app_android} onChange={v => update('mobile_app_android', v)} />
        </div>
      </Section>

      {/* Social Media */}
      <Section title="Social Media" icon={Users}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
          <Field label="Facebook URL" value={profile.social_facebook} onChange={v => update('social_facebook', v)} placeholder="https://facebook.com/..." />
          <Field label="Instagram URL" value={profile.social_instagram} onChange={v => update('social_instagram', v)} placeholder="https://instagram.com/..." />
          <Field label="LinkedIn URL" value={profile.social_linkedin} onChange={v => update('social_linkedin', v)} placeholder="https://linkedin.com/..." />
          <Field label="TikTok URL" value={profile.social_tiktok} onChange={v => update('social_tiktok', v)} placeholder="https://tiktok.com/@..." />
          <Field label="YouTube URL" value={profile.social_youtube} onChange={v => update('social_youtube', v)} placeholder="https://youtube.com/..." />
          <Field label="Twitter/X URL" value={profile.social_twitter} onChange={v => update('social_twitter', v)} placeholder="https://x.com/..." />
          <Field label="Rumble URL" value={profile.social_rumble} onChange={v => update('social_rumble', v)} placeholder="https://rumble.com/..." />
          <Field label="Truth Social URL" value={profile.social_truth} onChange={v => update('social_truth', v)} placeholder="https://truthsocial.com/@..." />
          <Field label="Pinterest URL" value={profile.social_pinterest} onChange={v => update('social_pinterest', v)} placeholder="https://pinterest.com/..." />
          <Field label="Nextdoor URL" value={profile.social_nextdoor} onChange={v => update('social_nextdoor', v)} />
        </div>
      </Section>

      {/* Business Hours */}
      <Section title="Business Hours" icon={Clock}>
        <div className="space-y-2 mt-3">
          {DAYS.map(day => {
            const h = profile.hours?.[day] || {};
            return (
              <div key={day} className="flex items-center gap-3">
                <span className="text-sm text-gray-300 w-24 capitalize">{day}</span>
                <Toggle label="" checked={!h.closed} onChange={v => updateHours(day, 'closed', !v)} />
                {!h.closed && (
                  <>
                    <Input value={h.open || ''} onChange={e => updateHours(day, 'open', e.target.value)} type="time"
                      className="bg-gray-50 border-gray-200 text-gray-900 text-xs h-8 w-28" />
                    <span className="text-gray-500 text-xs">to</span>
                    <Input value={h.close || ''} onChange={e => updateHours(day, 'close', e.target.value)} type="time"
                      className="bg-gray-50 border-gray-200 text-gray-900 text-xs h-8 w-28" />
                  </>
                )}
                {h.closed && <span className="text-xs text-gray-500">Closed</span>}
              </div>
            );
          })}
        </div>
      </Section>

      {/* Enhanced Content */}
      <Section title="Enhanced Content" icon={FileText}>
        <div className="space-y-3 mt-3">
          <TextArea label="Business Description" value={profile.description} onChange={v => update('description', v)} rows={4} placeholder="Describe your business..." />
          <TextArea label="Short Description" value={profile.short_description} onChange={v => update('short_description', v)} rows={2} placeholder="Brief tagline..." />
          <ListEditor label="Categories" items={profile.categories || []} onChange={v => update('categories', v)} placeholder="e.g., Plumbing, HVAC..." />
          <ListEditor label="Services" items={profile.services || []} onChange={v => update('services', v)} placeholder="e.g., Drain Cleaning, Pipe Repair..." />
          <ListEditor label="Products" items={profile.products || []} onChange={v => update('products', v)} placeholder="e.g., Water Heaters, Faucets..." />
          <ListEditor label="Payment Methods" items={profile.payment_methods || []} onChange={v => update('payment_methods', v)} placeholder="e.g., Cash, Visa, Mastercard..." />
          <ListEditor label="Languages Spoken" items={profile.languages || []} onChange={v => update('languages', v)} placeholder="e.g., English, Spanish..." />
        </div>
      </Section>

      {/* FAQs */}
      <Section title="Frequently Asked Questions" icon={MessageSquare}>
        <div className="space-y-3 mt-3">
          {(profile.faqs || []).map((faq, i) => (
            <div key={i} className="bg-gray-50 rounded-lg p-3 space-y-2">
              <div className="flex items-start gap-2">
                <div className="flex-1">
                  <Field label="Question" value={faq.question} onChange={v => {
                    const faqs = [...(profile.faqs || [])]; faqs[i] = { ...faqs[i], question: v }; update('faqs', faqs);
                  }} />
                </div>
                <Button size="sm" variant="ghost" onClick={() => update('faqs', (profile.faqs || []).filter((_, j) => j !== i))} className="text-gray-500 hover:text-red-400 mt-5">
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
              <TextArea label="Answer" value={faq.answer} onChange={v => {
                const faqs = [...(profile.faqs || [])]; faqs[i] = { ...faqs[i], answer: v }; update('faqs', faqs);
              }} rows={2} />
            </div>
          ))}
          <Button size="sm" onClick={() => update('faqs', [...(profile.faqs || []), { question: '', answer: '' }])} className="bg-gray-800 hover:bg-gray-700 text-gray-300 text-xs">
            <Plus className="h-3 w-3 mr-1" /> Add FAQ
          </Button>
        </div>
      </Section>

      {/* Accessibility & Amenities */}
      <Section title="Accessibility & Amenities" icon={Accessibility}>
        <div className="space-y-3 mt-3">
          <Toggle label="Wheelchair Accessible" checked={profile.wheelchair_accessible} onChange={v => update('wheelchair_accessible', v)} />
          <TextArea label="Accessibility Notes" value={profile.accessibility_notes} onChange={v => update('accessibility_notes', v)} rows={2} />
        </div>
      </Section>

      {/* Order & Delivery */}
      <Section title="Order, Pickup & Delivery" icon={Truck}>
        <div className="space-y-3 mt-3">
          <div className="flex flex-wrap gap-6">
            <Toggle label="Offers Delivery" checked={profile.offers_delivery} onChange={v => update('offers_delivery', v)} />
            <Toggle label="Offers Takeout" checked={profile.offers_takeout} onChange={v => update('offers_takeout', v)} />
            <Toggle label="Offers Pickup" checked={profile.offers_pickup} onChange={v => update('offers_pickup', v)} />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label="Delivery Area" value={profile.delivery_area} onChange={v => update('delivery_area', v)} placeholder="e.g., Within 15 miles" />
            <Field label="Online Order URL" value={profile.order_url} onChange={v => update('order_url', v)} />
            <Field label="Menu URL" value={profile.menu_url} onChange={v => update('menu_url', v)} />
            <Field label="Delivery URL" value={profile.delivery_url} onChange={v => update('delivery_url', v)} />
          </div>
          <ListEditor label="Cuisine Types" items={profile.cuisine_types || []} onChange={v => update('cuisine_types', v)} placeholder="e.g., American, Mexican..." />
        </div>
      </Section>

      {/* Competitors */}
      <Section title="Competitors" icon={Target}>
        <div className="space-y-3 mt-3">
          {(profile.competitors || []).map((comp, i) => (
            <div key={i} className="bg-gray-50 rounded-lg p-3 flex gap-3 items-start">
              <div className="flex-1 grid grid-cols-1 md:grid-cols-3 gap-2">
                <Field label="Name" value={comp.name} onChange={v => {
                  const c = [...(profile.competitors || [])]; c[i] = { ...c[i], name: v }; update('competitors', c);
                }} />
                <Field label="Website" value={comp.website} onChange={v => {
                  const c = [...(profile.competitors || [])]; c[i] = { ...c[i], website: v }; update('competitors', c);
                }} />
                <Field label="Notes" value={comp.notes} onChange={v => {
                  const c = [...(profile.competitors || [])]; c[i] = { ...c[i], notes: v }; update('competitors', c);
                }} />
              </div>
              <Button size="sm" variant="ghost" onClick={() => update('competitors', (profile.competitors || []).filter((_, j) => j !== i))} className="text-gray-500 hover:text-red-400 mt-5">
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </div>
          ))}
          <Button size="sm" onClick={() => update('competitors', [...(profile.competitors || []), { name: '', website: '', notes: '' }])} className="bg-gray-800 hover:bg-gray-700 text-gray-300 text-xs">
            <Plus className="h-3 w-3 mr-1" /> Add Competitor
          </Button>
        </div>
      </Section>

      {/* Review Generation */}
      <Section title="Review Generation" icon={Star}>
        <div className="space-y-3 mt-3">
          <Toggle label="Review Generation Enabled" checked={profile.review_generation_enabled} onChange={v => update('review_generation_enabled', v)} />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label="Google Review Link" value={profile.review_link_google} onChange={v => update('review_link_google', v)} placeholder="https://g.page/..." />
            <Field label="Facebook Review Link" value={profile.review_link_facebook} onChange={v => update('review_link_facebook', v)} />
            <Field label="Yelp Review Link" value={profile.review_link_yelp} onChange={v => update('review_link_yelp', v)} />
            <Field label="BBB Review Link" value={profile.review_link_bbb} onChange={v => update('review_link_bbb', v)} />
          </div>
        </div>
      </Section>

      {/* Service Area & Licensing */}
      <Section title="Service Area & Licensing" icon={MapPin}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
          <Field label="Service Area" value={profile.service_area} onChange={v => update('service_area', v)} placeholder="e.g., Dallas-Fort Worth Metroplex" />
          <Field label="Service Area Radius" value={profile.service_area_radius} onChange={v => update('service_area_radius', v)} placeholder="e.g., 25 miles" />
          <Field label="License Number" value={profile.license_number} onChange={v => update('license_number', v)} />
          <Field label="Insurance Info" value={profile.insurance_info} onChange={v => update('insurance_info', v)} />
        </div>
      </Section>
    </div>
  );
}
